import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { PlusIcon, PencilIcon, TrashIcon, MagnifyingGlassIcon, XMarkIcon, WrenchScrewdriverIcon } from '@heroicons/react/24/outline';
import { inventoryAPI } from '../../../services/inventoryApi';
import { useAuth } from '../../../contexts/AuthContext';
import ConfirmationModal from '../../Common/ConfirmationModal';
import SearchableLedgerDropdown from '../../Common/SearchableLedgerDropdown';

const EMPTY = { name: '', category: '', uom: 'JOB', description: '', expenseAccountId: '', expenseAccountLabel: '' };
const TABS = [{ key: 'true', label: 'Active' }, { key: 'false', label: 'Inactive' }, { key: '', label: 'All' }];

/**
 * Inventory → Services: the services bought from suppliers. A service's expense account is what its
 * purchase requests / orders are budget-checked against and what its service completion debits;
 * without one, the SERVICE_GRN ledger mapping account (Finance → Transaction Ledger Mapping) is used.
 */
const ServiceManagement = () => {
    const { hasPermission } = useAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [active, setActive] = useState('true');
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [pagination, setPagination] = useState({ total: 0, pages: 1 });
    const [form, setForm] = useState(null); // { ...EMPTY, _id? }
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);

    const canCreate = hasPermission(['INVENTORY:SERVICES:CREATE']);
    const canUpdate = hasPermission(['INVENTORY:SERVICES:UPDATE']);
    const canDelete = hasPermission(['INVENTORY:SERVICES:DELETE']);

    useEffect(() => { const t = setTimeout(() => { setSearch(searchInput.trim()); setPage(1); }, 400); return () => clearTimeout(t); }, [searchInput]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await inventoryAPI.getServices({ page, limit: 20, isActive: active || undefined, search: search || undefined, sortBy: 'createdAt', sortOrder: 'desc' });
            setItems(res.data || []);
            setPagination(res.pagination || { total: 0, pages: 1 });
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to load services');
        } finally { setLoading(false); }
    }, [page, active, search]);

    useEffect(() => { load(); }, [load]);

    const openForm = (s = null) => {
        setErrors({});
        setForm(s ? {
            _id: s._id, code: s.code, name: s.name || '', category: s.category || '', uom: s.uom || 'JOB', description: s.description || '',
            expenseAccountId: s.expenseAccount?.accountId ? String(s.expenseAccount.accountId) : '', expenseAccountLabel: s.expenseAccount?.label || '',
        } : { ...EMPTY });
    };

    const save = async () => {
        if (!form.name.trim()) { setErrors({ name: 'Service name is required' }); return; }
        setSaving(true);
        const payload = {
            name: form.name.trim(), category: form.category.trim(), uom: form.uom.trim() || 'JOB', description: form.description.trim(),
            expenseAccount: form.expenseAccountId ? { accountId: form.expenseAccountId, label: form.expenseAccountLabel } : null,
        };
        try {
            const res = form._id ? await inventoryAPI.updateService(form._id, payload) : await inventoryAPI.createService(payload);
            toast.success(res.message || 'Service saved');
            setForm(null);
            load();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to save the service');
        } finally { setSaving(false); }
    };

    const toggle = async (s) => {
        try {
            const res = await inventoryAPI.toggleServiceStatus(s._id);
            toast.success(res.message || 'Status changed');
            load();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to change the status');
        }
    };

    const remove = async () => {
        try {
            const res = await inventoryAPI.deleteService(deleteTarget._id);
            toast.success(res.message || 'Service deleted');
            setDeleteTarget(null);
            load();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to delete the service');
        }
    };

    const input = 'w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500';
    const label = 'block text-xs font-medium text-gray-600 mb-1';

    return (
        <div className="bg-white shadow-sm border border-gray-200">
            <div className="px-4 sm:px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 flex items-center justify-center">
                        <WrenchScrewdriverIcon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                        <h1 className="text-lg font-semibold text-gray-900">Services</h1>
                        <p className="text-xs text-gray-500">Services bought from suppliers — {pagination.total} {active === 'true' ? 'active' : active === 'false' ? 'inactive' : 'in total'}</p>
                    </div>
                </div>
                {canCreate && (
                    <button onClick={() => openForm()} className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-purple-600 rounded-md hover:from-indigo-700 hover:to-purple-700">
                        <PlusIcon className="w-4 h-4 mr-1" /> Add Service
                    </button>
                )}
            </div>

            <div className="px-4 sm:px-6 pt-3 flex flex-col md:flex-row md:items-center md:justify-between gap-3 border-b border-gray-200">
                <div className="flex">
                    {TABS.map(t => (
                        <button key={t.key || 'all'} role="tab" aria-selected={active === t.key} onClick={() => { setActive(t.key); setPage(1); }}
                            className={`px-3 py-2 text-xs font-medium border-b-2 ${active === t.key ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                            {t.label}
                        </button>
                    ))}
                </div>
                <div className="relative pb-2">
                    <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 absolute left-2 top-2" />
                    <input value={searchInput} onChange={e => setSearchInput(e.target.value)} placeholder="Search code, name, category…"
                        className="pl-7 pr-2 py-1.5 text-sm border border-gray-300 rounded-md w-72 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500" />
                </div>
            </div>

            <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                    <thead className="bg-gray-50">
                        <tr>
                            {['Code', 'Service', 'Category', 'Unit', 'Expense Account', 'Status', ''].map(h => (
                                <th key={h} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200" data-testid="service-rows">
                        {loading ? (
                            <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-400">Loading services…</td></tr>
                        ) : items.length === 0 ? (
                            <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-500">No services found.</td></tr>
                        ) : items.map(s => (
                            <tr key={s._id} className="hover:bg-gray-50">
                                <td className="px-4 py-2 font-mono text-xs text-indigo-700 whitespace-nowrap">{s.code}</td>
                                <td className="px-4 py-2">
                                    <div className="font-medium text-gray-900">{s.name}</div>
                                    {s.description && <div className="text-xs text-gray-500 max-w-md truncate">{s.description}</div>}
                                </td>
                                <td className="px-4 py-2 text-gray-700">{s.category || '—'}</td>
                                <td className="px-4 py-2 text-gray-700">{s.uom}</td>
                                <td className="px-4 py-2">
                                    {s.expenseAccount?.accountId
                                        ? <span className="text-gray-900">{s.expenseAccount.label || 'Set'}</span>
                                        : <span className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded px-1.5 py-0.5" title="Finance → Transaction Ledger Mapping → Service Completion">SERVICE_GRN mapping (default)</span>}
                                </td>
                                <td className="px-4 py-2">
                                    <button onClick={() => canUpdate && toggle(s)} disabled={!canUpdate} title={canUpdate ? (s.isActive ? 'Deactivate' : 'Activate') : ''}
                                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${s.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                                        {s.isActive ? 'Active' : 'Inactive'}
                                    </button>
                                </td>
                                <td className="px-4 py-2 text-right whitespace-nowrap">
                                    {canUpdate && <button title="Edit service" onClick={() => openForm(s)} className="p-1 text-gray-400 hover:text-indigo-600"><PencilIcon className="w-4 h-4" /></button>}
                                    {canDelete && <button title="Delete service" onClick={() => setDeleteTarget(s)} className="p-1 text-gray-400 hover:text-red-600"><TrashIcon className="w-4 h-4" /></button>}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="px-4 sm:px-6 py-3 flex items-center justify-between border-t border-gray-200 text-xs text-gray-600">
                <span>Page {page} of {pagination.pages || 1}</span>
                <div className="flex gap-2">
                    <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1 border border-gray-300 rounded disabled:opacity-40">Prev</button>
                    <button disabled={page >= (pagination.pages || 1)} onClick={() => setPage(p => p + 1)} className="px-3 py-1 border border-gray-300 rounded disabled:opacity-40">Next</button>
                </div>
            </div>

            {form && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
                        <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-t-lg">
                            <h3 className="text-base font-semibold text-white">{form._id ? `Edit Service ${form.code}` : 'Add Service'}</h3>
                            <button onClick={() => setForm(null)} aria-label="Close" className="text-white/80 hover:text-white"><XMarkIcon className="w-5 h-5" /></button>
                        </div>
                        <div className="p-4 space-y-3">
                            <div>
                                <label className={label}>Service Name <span className="text-red-500">*</span></label>
                                <input className={`${input} ${errors.name ? 'border-red-400' : ''}`} value={form.name} onChange={e => { setForm(f => ({ ...f, name: e.target.value })); setErrors({}); }} placeholder="e.g. AC Maintenance" />
                                {errors.name && <p className="text-xs text-red-500 mt-0.5">{errors.name}</p>}
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className={label}>Category</label>
                                    <input className={input} value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} placeholder="e.g. Maintenance" />
                                </div>
                                <div>
                                    <label className={label}>Unit</label>
                                    <input className={input} value={form.uom} onChange={e => setForm(f => ({ ...f, uom: e.target.value }))} placeholder="JOB, VISIT, HOUR, MONTH…" />
                                </div>
                            </div>
                            <div>
                                <label className={label}>Expense Account</label>
                                <SearchableLedgerDropdown
                                    value={form.expenseAccountId}
                                    displayLabel={form.expenseAccountLabel}
                                    onChange={(id, lbl) => setForm(f => ({ ...f, expenseAccountId: id || '', expenseAccountLabel: lbl || '' }))}
                                    ledgerGroup="EXPENSES"
                                    placeholder="Select expense account…"
                                    menuPlacement="auto"
                                />
                                <p className="text-xs text-gray-500 mt-0.5">Budget-checked and debited when the service is completed. Leave empty to use the SERVICE_GRN mapping account (Finance → Transaction Ledger Mapping).</p>
                            </div>
                            <div>
                                <label className={label}>Description</label>
                                <textarea rows={2} className={`${input} resize-none`} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 px-4 py-3 border-t border-gray-200 bg-gray-50 rounded-b-lg">
                            <button onClick={() => setForm(null)} className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
                            <button onClick={save} disabled={saving} className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50">
                                {saving ? 'Saving…' : form._id ? 'Update Service' : 'Create Service'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <ConfirmationModal
                isOpen={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onConfirm={remove}
                title="Delete Service"
                message={deleteTarget ? `Delete ${deleteTarget.code} — ${deleteTarget.name}?` : ''}
                confirmText="Delete"
                type="danger"
            />
        </div>
    );
};

export default ServiceManagement;
